import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { MoneyInput } from '../components/MoneyInput';
import { toast } from 'sonner';
import { Download, Trash2, ChevronLeft, ChevronRight, Pencil, CalendarIcon, Save } from 'lucide-react';
import { format } from 'date-fns';

const IntInput = ({ label, name, value, onChange, required, helper }) => (
  <div className="space-y-1">
    <Label className="text-slate-700 text-xs font-medium">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    <Input type="number" min="0"
      value={value === null || value === undefined ? '' : value}
      onChange={e => onChange(name, e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
      className="h-8 text-sm bg-white" />
    {helper && <p className="text-xs text-slate-400">{helper}</p>}
  </div>
);

export default function EODRecordsPage() {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [locations, setLocations] = useState([]);
  const [filters, setFilters] = useState({ location_id: '', date_from: '', date_to: '' });
  const [editRecord, setEditRecord] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editDateOpen, setEditDateOpen] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const limit = 25;

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (filters.location_id) params.location_id = filters.location_id;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const res = await axios.get(`${API}/eod`, { params });
      setRecords(res.data.records);
      setTotal(res.data.total);
    } catch { toast.error('Failed to load records'); }
    finally { setLoading(false); }
  }, [page, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchRecords(); }, [fetchRecords]);
  useEffect(() => { axios.get(`${API}/locations`).then(r => setLocations(r.data)).catch(() => {}); }, []);

  const handleExport = () => {
    const params = new URLSearchParams();
    if (filters.location_id) params.append('location_id', filters.location_id);
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    const token = localStorage.getItem('arch_token');
    fetch(`${API}/export/eod?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
        a.download = 'eod_records.csv'; a.click();
      });
  };

  const openEdit = (r) => {
    setEditForm({
      submission_date: r.submission_date ? r.submission_date.slice(0, 10) : '',
      location_id: r.location_id || '',
      inbound_calls: r.inbound_calls ?? '',
      calls_answered: r.calls_answered ?? '',
      appointments_scheduled: r.appointments_scheduled ?? '',
      total_consults_scheduled: r.total_consults_scheduled ?? '',
      total_consults_showed: r.total_consults_showed ?? '',
      full_arch_cases_presented: r.full_arch_cases_presented ?? '',
      non_full_arch_cases_presented: r.non_full_arch_cases_presented ?? '',
      closed_today_from_today: r.closed_today_from_today ?? '',
      full_arch_sold_today_from_today: r.full_arch_sold_today_from_today ?? '',
      arches_sold_today_from_today: r.arches_sold_today_from_today ?? '',
      total_treatments_sold_today_from_today: r.total_treatments_sold_today_from_today ?? '',
      closed_from_previous: r.closed_from_previous ?? '',
      full_arch_sold_from_previous: r.full_arch_sold_from_previous ?? '',
      arches_sold_from_previous: r.arches_sold_from_previous ?? '',
      non_full_arch_from_previous: r.non_full_arch_from_previous ?? '',
      total_treatments_sold_from_previous: r.total_treatments_sold_from_previous ?? '',
      total_collections_today: r.total_collections_today ?? '',
      surgical_production_today: r.surgical_production_today ?? '',
      notes: r.notes || '',
    });
    setEditRecord(r);
  };

  const setEditField = (name, val) => setEditForm(prev => ({ ...prev, [name]: val }));

  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      const payload = {
        ...editForm,
        inbound_calls: parseInt(editForm.inbound_calls) || 0,
        calls_answered: parseInt(editForm.calls_answered) || 0,
        appointments_scheduled: parseInt(editForm.appointments_scheduled) || 0,
        total_consults_scheduled: parseInt(editForm.total_consults_scheduled) || 0,
        total_consults_showed: parseInt(editForm.total_consults_showed) || 0,
        full_arch_cases_presented: parseInt(editForm.full_arch_cases_presented) || 0,
        non_full_arch_cases_presented: parseInt(editForm.non_full_arch_cases_presented) || 0,
        closed_today_from_today: parseInt(editForm.closed_today_from_today) || 0,
        full_arch_sold_today_from_today: parseInt(editForm.full_arch_sold_today_from_today) || 0,
        arches_sold_today_from_today: parseInt(editForm.arches_sold_today_from_today) || 0,
        total_treatments_sold_today_from_today: parseInt(editForm.total_treatments_sold_today_from_today) || 0,
        closed_from_previous: parseInt(editForm.closed_from_previous) || 0,
        full_arch_sold_from_previous: parseInt(editForm.full_arch_sold_from_previous) || 0,
        arches_sold_from_previous: parseInt(editForm.arches_sold_from_previous) || 0,
        non_full_arch_from_previous: parseInt(editForm.non_full_arch_from_previous) || 0,
        total_treatments_sold_from_previous: parseInt(editForm.total_treatments_sold_from_previous) || 0,
        total_collections_today: parseFloat(editForm.total_collections_today) || 0,
        surgical_production_today: parseFloat(editForm.surgical_production_today) || 0,
      };
      await axios.put(`${API}/eod/${editRecord.id}`, payload);
      toast.success('EOD record updated');
      setEditRecord(null);
      fetchRecords();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to update'); }
    finally { setSavingEdit(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this EOD record?')) return;
    try { await axios.delete(`${API}/eod/${id}`); toast.success('Deleted'); fetchRecords(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed to delete'); }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">EOD Records</h1>
          <p className="text-slate-500 text-sm mt-1">{total} total records</p>
        </div>
        <Button onClick={handleExport} className="gap-2 bg-white text-slate-800 border border-slate-200 hover:bg-slate-50" data-testid="eod-table-export-button">
          <Download size={14} /> Export CSV
        </Button>
      </div>
      <Card className="bg-white shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3">
            <Select value={filters.location_id || 'all'} onValueChange={v => setFilters(f => ({ ...f, location_id: v === 'all' ? '' : v }))}>
              <SelectTrigger className="h-8 text-xs w-40 bg-white"><SelectValue placeholder="All Locations" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Locations</SelectItem>
                {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="date" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} className="h-8 text-xs w-36 bg-white" />
            <Input type="date" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} className="h-8 text-xs w-36 bg-white" />
            <Button size="sm" onClick={() => setFilters({ location_id: '', date_from: '', date_to: '' })} variant="ghost" className="h-8 text-xs">Clear</Button>
          </div>
        </CardContent>
      </Card>
      <Card className="bg-white shadow-sm">
        <CardContent className="p-0">
          <ScrollArea className="w-full">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-100">
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Date</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Location</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Scheduled</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Showed</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wide">FA Presented</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wide">FA Sold</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Total Collected</TableHead>
                  <TableHead className="w-20"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? ([...Array(5)].map((_, i) => (
                  <TableRow key={i}>{[...Array(8)].map((_, j) => <TableCell key={j}><Skeleton className="h-4" /></TableCell>)}</TableRow>
                ))) : records.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="py-12 text-center text-slate-400">No records found</TableCell></TableRow>
                ) : records.map(r => {
                  const locName = locations.find(l => l.id === r.location_id)?.name || r.location_id;
                  return (
                    <TableRow key={r.id} className="data-row border-slate-50">
                      <TableCell className="text-xs text-slate-600">{r.submission_date ? format(new Date(r.submission_date), 'MMM d, yyyy') : '-'}</TableCell>
                      <TableCell className="text-xs font-medium text-slate-800">{locName}</TableCell>
                      <TableCell className="text-xs text-slate-700">{r.total_consults_scheduled}</TableCell>
                      <TableCell className="text-xs text-slate-700">{r.total_consults_showed}</TableCell>
                      <TableCell className="text-xs font-medium" style={{ color: 'hsl(191, 95%, 35%)' }}>{r.full_arch_cases_presented}</TableCell>
                      <TableCell className="text-xs font-semibold text-slate-800">{(r.full_arch_sold_today_from_today || 0) + (r.full_arch_sold_from_previous || 0)}</TableCell>
                      <TableCell className="text-xs font-medium text-green-700">${(r.total_collections_today || 0).toLocaleString()}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hover:text-cyan-600" onClick={() => openEdit(r)}><Pencil size={12} /></Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hover:text-red-500" onClick={() => handleDelete(r.id)}><Trash2 size={12} /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </ScrollArea>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
              <span className="text-xs text-slate-500">Page {page} of {totalPages}</span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="h-7 w-7 p-0" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={13} /></Button>
                <Button size="sm" variant="outline" className="h-7 w-7 p-0" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight size={13} /></Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit EOD Dialog */}
      <Dialog open={!!editRecord} onOpenChange={o => !o && setEditRecord(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit EOD Record</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Date</Label>
                <Popover open={editDateOpen} onOpenChange={setEditDateOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left text-xs bg-white h-8">
                      <CalendarIcon size={12} className="mr-2" />
                      {editForm.submission_date ? format(new Date(editForm.submission_date + 'T00:00:00'), 'MMM d, yyyy') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white">
                    <Calendar mode="single"
                      selected={editForm.submission_date ? new Date(editForm.submission_date + 'T00:00:00') : undefined}
                      onSelect={d => { if (d) { setEditField('submission_date', format(d, 'yyyy-MM-dd')); setEditDateOpen(false); } }} />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Location</Label>
                <Select value={editForm.location_id} onValueChange={v => setEditField('location_id', v)}>
                  <SelectTrigger className="h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <IntInput label="Inbound Calls" name="inbound_calls" value={editForm.inbound_calls} onChange={setEditField} />
              <IntInput label="Calls Answered" name="calls_answered" value={editForm.calls_answered} onChange={setEditField} />
              <IntInput label="Appts Scheduled" name="appointments_scheduled" value={editForm.appointments_scheduled} onChange={setEditField} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <IntInput label="Consults Scheduled *" name="total_consults_scheduled" value={editForm.total_consults_scheduled} onChange={setEditField} />
              <IntInput label="Consults Showed *" name="total_consults_showed" value={editForm.total_consults_showed} onChange={setEditField} />
              <IntInput label="FA Presented *" name="full_arch_cases_presented" value={editForm.full_arch_cases_presented} onChange={setEditField} />
              <IntInput label="Non-FA Presented" name="non_full_arch_cases_presented" value={editForm.non_full_arch_cases_presented} onChange={setEditField} />
            </div>
            <p className="text-xs font-semibold text-slate-700 border-t pt-2">Today's Outcomes</p>
            <div className="grid grid-cols-2 gap-3">
              <IntInput label="Closed (Today's)" name="closed_today_from_today" value={editForm.closed_today_from_today} onChange={setEditField} />
              <IntInput label="FA Sold (Same Day)" name="full_arch_sold_today_from_today" value={editForm.full_arch_sold_today_from_today} onChange={setEditField} />
              <IntInput label="Arches (Same Day)" name="arches_sold_today_from_today" value={editForm.arches_sold_today_from_today} onChange={setEditField} />
              <IntInput label="Treatments (Same Day)" name="total_treatments_sold_today_from_today" value={editForm.total_treatments_sold_today_from_today} onChange={setEditField} />
            </div>
            <p className="text-xs font-semibold text-slate-700 border-t pt-2">Previous Consult Outcomes</p>
            <div className="grid grid-cols-2 gap-3">
              <IntInput label="Closed (Previous)" name="closed_from_previous" value={editForm.closed_from_previous} onChange={setEditField} />
              <IntInput label="FA Sold (Previous)" name="full_arch_sold_from_previous" value={editForm.full_arch_sold_from_previous} onChange={setEditField} />
              <IntInput label="Arches (Previous)" name="arches_sold_from_previous" value={editForm.arches_sold_from_previous} onChange={setEditField} />
              <IntInput label="Treatments (Previous)" name="total_treatments_sold_from_previous" value={editForm.total_treatments_sold_from_previous} onChange={setEditField} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Total Collected</Label>
                <MoneyInput value={editForm.total_collections_today} onValueChange={v => setEditField('total_collections_today', v)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Surgical Production</Label>
                <MoneyInput value={editForm.surgical_production_today} onValueChange={v => setEditField('surgical_production_today', v)} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="bg-white" onClick={() => setEditRecord(null)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={savingEdit}
              style={{ background: 'hsl(191,95%,43%)', color: 'hsl(222,47%,11%)' }}>
              <Save size={13} className="mr-1.5" />{savingEdit ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
